import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import SEO from '../components/SEO';

const BinauralBeatsInfoPage: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <div className='min-h-screen' style={{ background: 'rgb(8 6 20 / 100%)' }}>
      <SEO
        title={t('seo.research.title')}
        description={t('seo.research.description')}
        canonical='/binaural-info'
      />
      <Header showUserInfo={false} variant='player' />

      <div className='page'>
        <div className='container-mobile'>
          <BackButton />
          <div className='text-center mb-8'>
            <div
              className='subtitle'
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <div className='flex items-center justify-center mb-4 md:mb-6'>
                <h1
                  className='text-2xl sm:text-3xl md:text-4xl font-bold'
                  style={{
                    color: 'var(--text-primary)',
                    fontFamily: 'Lexend Deca, sans-serif',
                  }}
                >
                  Respaldado por la Ciencia
                </h1>
              </div>
              <p
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  width: '70%',
                }}
              >
                Estos son algunos de los estudios científicos que reportan sobre
                la efectividad de los sonidos binaurales. Investigación
                publicada en revistas de alto impacto validando los efectos
                neurológicos de los sonidos binaurales en estudios controlados.
              </p>
            </div>
          </div>
          <section className='section'>
            <div className='container-mobile'>
              <div className='study-card'>
                <div className='evidence-studies'>
                  <div className='studies-grid'>
                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>Meta-análisis</span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          Efficacy of binaural auditory beats in cognition,
                          anxiety, and pain perception: a meta-analysis
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Garcia-Argibay, M., Santed, M. A., & Reales, J. M.
                          </span>
                          <span className='source-year'>2019</span>
                          <a
                            href='https://pubmed.ncbi.nlm.nih.gov/30073406/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver en PubMed
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>Revisión Sistemática</span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          The Efficiency of Binaural Beats on Anxiety and
                          Depression—A Systematic Review
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Baseanu, I. C. C., et al.
                          </span>
                          <span className='source-year'>2024</span>
                          <a
                            href='https://www.mdpi.com/2076-3417/14/13/5675'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver artículo completo (MDPI)
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>Revisión Sistemática</span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          The effect of binaural beats on stress and anxiety: a
                          systematic review
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Chaieb, L., Maag, K., Feld, F., & Koenig, J.
                          </span>
                          <span className='source-year'>2015</span>
                          <a
                            href='https://www.researchgate.net/publication/370900678_Binaural_beats_to_entrain_the_brain_A_systematic_review_of_the_effects_of_binaural_beat_stimulation_on_brain_oscillatory_activity_and_the_implications_for_psychological_research_and_intervention'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver en ResearchGate
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>Estudio Clínico</span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          A prospective, randomised, controlled study examining
                          binaural beat audio and pre-operative anxiety in
                          patients undergoing general anaesthesia
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Padmanabhan, R., Hildreth, A. J., & Laws, D.
                          </span>
                          <span className='source-year'>2005</span>
                          <a
                            href='https://pubmed.ncbi.nlm.nih.gov/16115248/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver en PubMed
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>Estudio</span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          The Effectiveness of Alpha Binaural Beats in Reducing
                          Stress and Rumination and Promoting Sleep Quality in
                          University Students
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Kandiah, J., et al.
                          </span>
                          <span className='source-year'>2023</span>
                          <a
                            href='https://jss.tums.ac.ir/index.php/jss/article/view/208/203'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver en Journal of Sleep Sciences
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>Estudio Experimental</span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          Binaural auditory beats affect vigilance performance
                          and mood
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Lane, R. D., et al.
                          </span>
                          <span className='source-year'>1998</span>
                          <a
                            href='https://pubmed.ncbi.nlm.nih.gov/9423966/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver en PubMed
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>
                          Revisión de Literatura
                        </span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          Effects of binaural beats and isochronic tones on
                          brain wave modulation: Literature review
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            De Oliveira, N. F. T. V., et al.
                          </span>
                          <span className='source-year'>2021</span>
                          <a
                            href='https://www.scielo.org.mx/scielo.php?script=sci_arttext&pid=S1665-50442021000600238'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver en Revista Mexicana de Neurociencia
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className='study-card'>
                      <div className='study-badge'>
                        <span className='study-type'>EEG</span>
                      </div>
                      <div className='study-header'>
                        <h4 className='study-title'>
                          Significance of alpha brainwaves in meditation
                          examined from the study of binaural beats
                        </h4>
                      </div>

                      <div className='study-source'>
                        <div className='source-details'>
                          <span className='source-authors'>
                            Gupta, A., Ramdinmawii, E., & Mittal, V. K.
                          </span>
                          <span className='source-year'>2016</span>
                          <a
                            href='https://ieeexplore.ieee.org/document/7980629/'
                            target='_blank'
                            rel='noopener noreferrer'
                            className='source-link'
                          >
                            Ver en IEEE Xplore
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='evidence-action'>
                  <p className='evidence-note'>
                    Estudios publicados en Neuropsychologia, Applied Psychology,
                    Sleep Medicine Reviews y más
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BinauralBeatsInfoPage;
